import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { downloadFile } from '../../ui/downloadFile.js'
import { EmptyCard, PagedScreen } from '../../ui/PagedScreen.js'
import { Td, Tr } from '../../ui/Table.js'
import { usePaged } from '../../ui/usePaged.js'

/**
 * Who a segment reaches right now.
 *
 * "Right now" is the whole of it. A segment stores rules, so this list is an
 * answer rather than a record, and it is resolved by the same translation the
 * send path uses — the list and the audience cannot disagree about what the
 * rules mean.
 *
 * They can still differ about *who*, and the screen says so. Suppression is
 * re-read at the moment each message is claimed, so somebody listed here may be
 * skipped when the campaign actually runs. Leaving that unsaid is how a count
 * on this screen becomes a number somebody expects to see in the report.
 */
export function SegmentMembersPage({ client }: { readonly client: ApiClient }) {
  const { segmentId = '' } = useParams()
  const load = useCallback(
    (page: number) => client.segmentMembers(segmentId, page),
    [client, segmentId],
  )
  const { state, setPage, retry } = usePaged(
    load,
    'We could not load who this segment reaches. Please try again in a moment.',
  )

  const [problem, setProblem] = useState<string | undefined>(undefined)

  /** Fetches the file and hands it to the browser. `downloadFile` says how. */
  const download = useCallback(async () => {
    setProblem(undefined)
    try {
      downloadFile('segment.csv', await client.exportSegment(segmentId))
    } catch {
      setProblem('We could not build that file. Please try again in a moment.')
    }
  }, [client, segmentId])

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link className="font-medium text-[13px] text-t3 hover:text-t1" to="/segments">
          ← Segments
        </Link>
        <button
          type="button"
          onClick={() => void download()}
          className="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:bg-line-2 hover:text-t1"
        >
          Export CSV
        </button>
      </div>

      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <PagedScreen
        title="Who this reaches"
        intro="Worked out now, from the rules. Anyone suppressed when the campaign actually runs is skipped then, so this is the audience before that last check."
        state={state}
        onRetry={() => void retry()}
        onPage={setPage}
        head={['Email', 'Name']}
        minWidth={640}
        empty={
          <EmptyCard heading="Nobody matches">
            The rules are right, or the list is. Open the builder to see the reach change as you
            adjust them.
          </EmptyCard>
        }
        row={(member) => (
          <Tr key={member.contactId}>
            <Td strong>
              <Link className="text-brand" to={`/contacts/${member.contactId}`}>
                {member.email}
              </Link>
            </Td>
            <Td>{member.name === '' ? '—' : member.name}</Td>
          </Tr>
        )}
      />
    </>
  )
}
